import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { DialogModule } from 'primeng/dialog';
import { PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';

import { AuthService } from '@/core/auth/auth.service';
import { PrestadorEstatisticaAgrupadaResponse, PrestadorEstatisticaCadastroAnaliticoResponse } from '@/core/api/sicom-api/registro/prestador/controle-administrativo/prestador-estatistica-agrupada.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { EstatisticaMensalUtils } from '@/shared/utils/estatistica-mensal-utils';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';
import { PrestadorControleAdministrativoFeatureService } from './prestador-controle-administrativo.service';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type EstatisticaPrestadorId =
    | 'situacoes'
    | 'ativos-registro-cadastro'
    | 'novas-inscricoes'
    | 'municipios'
    | 'tipos-estabelecimento'
    | 'categorias'
    | 'naturezas'
    | 'enderecos-ausentes'
    | 'certificados-situacao'
    | 'certificados-vencimento-faixa'
    | 'renovacoes-certificado'
    | 'tempo-renovacao-certificado'
    | 'sem-diretor-tecnico'
    | 'diretores-tecnicos-vinculos'
    | 'diretores-tecnicos-faixa-etaria'
    | 'diretoria-duplicada'
    | 'razao-medicos-prestadores'
    | 'multivinculo-corpo-clinico'
    | 'corpo-clinico-tamanho'
    | 'corpo-clinico-vinculo'
    | 'comissao-etica-vigente'
    | 'comissao-etica-vencimento'
    | 'comissao-etica-obrigatoria-ausente'
    | 'comissoes-obrigatorias-tipo'
    | 'cnaes-principais'
    | 'sem-cnae-principal'
    | 'cnaes-secundarios'
    | 'servicos'
    | 'atividades'
    | 'capacidade-instalada-uf'
    | 'ciclo-vida-prestador'
    | 'recursos-humanos'
    | 'recursos-materiais'
    | 'especialidades'
    | 'sem-especialidade-obrigatoria'
    | 'pendencias-manuais'
    | 'pendencias-operacionais'
    | 'cnpj-duplicado'
    | 'faixa-capital-social'
    | 'enderecos-desatualizados'
    | 'vinculados-debito'
    | 'delegacias'
    | 'eleitorado-corpo-clinico'
    | 'socios-faixa'
    | 'socios-cargo'
    | 'filiais-matriz'
    | 'sem-cnes'
    | 'movimentacao-situacao-pj'
    | 'pre-cadastros'
    | 'chefias-servico'
    | 'servicos-sem-chefia'
    | 'documento-constituicao'
    | 'ensino'
    | 'gestao'
    | 'abrangencia'
    | 'corpo-clinico-entradas'
    | 'corpo-clinico-saidas'
    | 'anotacoes-prestador-tipo'
    | 'anotacoes-prestador-mes';

type EstatisticaVisualizacao = 'linha' | 'barra' | 'rosca';

type EstatisticaModoTotal = 'soma' | 'primeiro' | 'media';

type EstatisticaPrestadorGrupo = {
    id: EstatisticaPrestadorId;
    titulo: string;
    descricao: string;
    grupo: string;
    classeGrupo: string;
    rotuloTotal: string;
    visualizacao: EstatisticaVisualizacao;
    modoTotal?: EstatisticaModoTotal;
    linhaInteira?: boolean;
    itens: PrestadorEstatisticaAgrupadaResponse[] | null;
    carregando: boolean;
};

const ESTATISTICAS_MENSAIS_PRESTADOR: ReadonlySet<EstatisticaPrestadorId> = new Set<EstatisticaPrestadorId>([
    'novas-inscricoes',
    'renovacoes-certificado',
    'movimentacao-situacao-pj',
    'corpo-clinico-entradas',
    'corpo-clinico-saidas',
    'anotacoes-prestador-mes'
]);

const ESTATISTICAS_SEM_ANALITICO_PRESTADOR: ReadonlySet<EstatisticaPrestadorId> = new Set<EstatisticaPrestadorId>(['razao-medicos-prestadores', 'multivinculo-corpo-clinico', 'capacidade-instalada-uf', 'eleitorado-corpo-clinico', 'pre-cadastros']);

type AgrupadorPrestador = {
    slug: string;
    label: string;
    icon: string;
    descricao: string;
};

type SecaoEstatisticaPrestador = { titulo: string; mostrarTitulo: boolean; grupos: EstatisticaPrestadorGrupo[] };

@Component({
    selector: 'app-estatisticas-cadastro-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, CardModule, CardContent, SelectEstado, MapaUfCard, SkeletonModule, TagModule, ChartModule, DialogModule, DetalhamentoPendencias, PainelLateral],
    templateUrl: './estatisticas-cadastro-prestador.html',
    styleUrl: '../estatisticas-cadastro/estatisticas-cadastro.scss'
})
export class EstatisticasCadastroPrestador implements OnInit {
    readonly anoAtual = new Date().getFullYear();
    readonly limiteRanking = 10;
    readonly agrupadores: AgrupadorPrestador[] = [
        { slug: 'situacao', label: 'Situação', icon: 'pi pi-verified', descricao: 'Situação cadastral, entradas e classificação entre registro e cadastro.' },
        { slug: 'perfil-juridico', label: 'Perfil jurídico', icon: 'pi pi-building', descricao: 'Natureza, categoria e tipo de estabelecimento do prestador.' },
        { slug: 'localizacao', label: 'Localização', icon: 'pi pi-map-marker', descricao: 'Distribuição territorial dos prestadores por município de correspondência.' },
        { slug: 'certificados', label: 'Certificados', icon: 'pi pi-file-check', descricao: 'Validade e ausência de certificado PJ para prestadores ativos.' },
        { slug: 'diretoria', label: 'Diretoria', icon: 'pi pi-user-edit', descricao: 'Indicadores de diretoria técnica e corpo clínico vigente do prestador.' },
        { slug: 'comissao', label: 'Comissão', icon: 'pi pi-users', descricao: 'Comissão de ética e comissões obrigatórias vinculadas ao prestador.' },
        { slug: 'servicos', label: 'Serviços', icon: 'pi pi-briefcase', descricao: 'CNAEs, serviços, atividades e especialidades declaradas pelo prestador.' },
        { slug: 'operacional', label: 'Operacional', icon: 'pi pi-inbox', descricao: 'Anotações administrativas e movimentações operacionais do prestador.' },
        { slug: 'pendencias', label: 'Pendências', icon: 'pi pi-exclamation-triangle', descricao: 'Pendências manuais, operacionais e duplicidades cadastrais.' }
    ];

    anoSelecionado = this.anoAtual;
    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    grupoSelecionadoSlug = this.agrupadores[0].slug;
    grupoMaximizado: EstatisticaPrestadorGrupo | null = null;
    exibirModalMaximizados = false;
    estatisticaSelecionada: EstatisticaPrestadorGrupo | null = null;
    grupoAnaliticoCodigo: string | null = null;
    linhasAnalitico: PendenciaLinha[] = [];
    totalRegistrosAnalitico = 0;
    firstAnalitico = 0;
    rowsAnalitico = 100;
    carregandoAnalitico = false;

    estatisticas: EstatisticaPrestadorGrupo[] = [
        {
            id: 'situacoes',
            titulo: 'Prestadores por situação cadastral',
            descricao: 'Distribuição dos prestadores pela situação atual cadastrada no CNP.',
            grupo: 'Situação',
            classeGrupo: 'grupo-situacao',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'ativos-registro-cadastro',
            titulo: 'Ativos por registro ou cadastro',
            descricao: 'Prestadores ativos separados por registrados, cadastrados e não informados.',
            grupo: 'Situação',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'prestadores ativos',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'novas-inscricoes',
            titulo: 'Novas inscrições por mês',
            descricao: 'Volume mensal de novas inscrições de prestador no ano selecionado.',
            grupo: 'Situação',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'inscrições',
            visualizacao: 'linha',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'ciclo-vida-prestador',
            titulo: 'Ciclo de vida do prestador',
            descricao: 'Prestadores ativos distribuídos pelo tempo de vida desde a inscrição.',
            grupo: 'Situação',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        },
        {
            id: 'tipos-estabelecimento',
            titulo: 'Prestadores por tipo de estabelecimento',
            descricao: 'Distribuição entre único, matriz e tipos de filial.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'categorias',
            titulo: 'Categoria do prestador',
            descricao: 'Distribuição cadastral por categoria jurídica informada.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'naturezas',
            titulo: 'Natureza pública/privada',
            descricao: 'Prestadores agrupados pela natureza pública, privada ou não informada.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'faixa-capital-social',
            titulo: 'Prestadores por faixa de capital social',
            descricao: 'Distribuição dos prestadores ativos por faixa de valor do capital social informado.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'municipios',
            titulo: 'Prestadores por município',
            descricao: 'Ranking dos municípios com maior concentração de prestadores.',
            grupo: 'Localização',
            classeGrupo: 'grupo-endereco',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'delegacias',
            titulo: 'Prestadores por delegacia regional',
            descricao: 'Ranking das delegacias regionais pelo total de prestadores ativos do município de correspondência.',
            grupo: 'Localização',
            classeGrupo: 'grupo-endereco',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'certificados-situacao',
            titulo: 'Situação dos certificados PJ',
            descricao: 'Prestadores ativos agrupados pela situação do último certificado PJ.',
            grupo: 'Certificados',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'prestadores ativos',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'tempo-renovacao-certificado',
            titulo: 'Tempo médio de renovação',
            descricao: 'Atraso médio, em dias, entre validade anterior e renovação.',
            grupo: 'Certificados',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'dias',
            visualizacao: 'barra',
            modoTotal: 'media',
            itens: null,
            carregando: false
        },
        {
            id: 'certificados-vencimento-faixa',
            titulo: 'Certificados por faixa de vencimento',
            descricao: 'Faixas detalhadas de vencimento do último certificado PJ de prestadores ativos.',
            grupo: 'Certificados',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'prestadores ativos',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'renovacoes-certificado',
            titulo: 'Renovações por mês',
            descricao: 'Volume mensal de renovações de certificado PJ no ano selecionado.',
            grupo: 'Certificados',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'renovações',
            visualizacao: 'linha',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'sem-diretor-tecnico',
            titulo: 'Sem diretor técnico vigente',
            descricao: 'Prestadores ativos sem diretor técnico vigente cadastrado.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'diretores-tecnicos-vinculos',
            titulo: 'Diretores técnicos por vínculos',
            descricao: 'Médicos agrupados pela quantidade de diretorias técnicas vigentes em prestadores ativos.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'médicos',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'diretores-tecnicos-faixa-etaria',
            titulo: 'Diretores técnicos por faixa etária',
            descricao: 'Distribuição etária dos diretores técnicos vigentes de prestadores ativos.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'diretores',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'diretoria-duplicada',
            titulo: 'Diretoria duplicada',
            descricao: 'Registros vigentes duplicados para o mesmo médico, cargo e prestador.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'registros',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'corpo-clinico-tamanho',
            titulo: 'Tamanho do corpo clínico',
            descricao: 'Prestadores ativos distribuídos por faixas de quantidade de médicos no corpo clínico vigente.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'corpo-clinico-vinculo',
            titulo: 'Corpo clínico por vínculo',
            descricao: 'Médicos do corpo clínico vigente agrupados pela natureza do vínculo.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'vínculos',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'razao-medicos-prestadores',
            titulo: 'Médicos com e sem vínculo',
            descricao: 'Relação de médicos do corpo clínico vigente com e sem vínculo formal ao prestador.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'médicos',
            visualizacao: 'rosca',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        },
        {
            id: 'multivinculo-corpo-clinico',
            titulo: 'Multivínculo do corpo clínico',
            descricao: 'Médicos do corpo clínico vigente agrupados pela quantidade de prestadores em que atuam.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'médicos',
            visualizacao: 'barra',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        },
        {
            id: 'vinculados-debito',
            titulo: 'Vínculos com débito em aberto',
            descricao: 'Médicos com débito em aberto que são diretores técnicos, membros de comissão de ética ou corpo clínico vigente.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'médicos',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'comissao-etica-vigente',
            titulo: 'Comissão de ética vigente',
            descricao: 'Prestadores ativos agrupados pela existência de comissão de ética vigente.',
            grupo: 'Comissão',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'comissao-etica-obrigatoria-ausente',
            titulo: 'Comissão obrigatória ausente',
            descricao: 'Prestadores com 30 ou mais médicos no corpo clínico e sem comissão de ética vigente.',
            grupo: 'Comissão',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'comissao-etica-vencimento',
            titulo: 'Vencimento da comissão de ética',
            descricao: 'Prestadores ativos com comissão de ética agrupados por faixa de vencimento.',
            grupo: 'Comissão',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'comissoes-obrigatorias-tipo',
            titulo: 'Comissões obrigatórias por tipo',
            descricao: 'Prestadores ativos com comissões obrigatórias vigentes agrupadas por tipo.',
            grupo: 'Comissão',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'eleitorado-corpo-clinico',
            titulo: 'Eleitorado potencial por corpo clínico',
            descricao: 'Ranking de prestadores pelo total de médicos ativos vinculados ao corpo clínico vigente (eleitorado potencial pré-pleito).',
            grupo: 'Comissão',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'médicos',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'cnaes-principais',
            titulo: 'Ranking de CNAEs principais',
            descricao: 'CNAEs principais vigentes mais usados por prestadores ativos.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'sem-cnae-principal',
            titulo: 'Sem CNAE principal',
            descricao: 'Prestadores ativos sem CNAE principal vigente.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'cnaes-secundarios',
            titulo: 'CNAEs secundários por prestador',
            descricao: 'Distribuição de prestadores ativos pela quantidade de CNAEs secundários vigentes.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'servicos',
            titulo: 'Serviços mais declarados',
            descricao: 'Ranking de serviços vigentes declarados por prestadores ativos.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'atividades',
            titulo: 'Atividades desenvolvidas',
            descricao: 'Ranking de atividades vigentes cadastradas para prestadores ativos.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'recursos-humanos',
            titulo: 'Recursos humanos por tipo',
            descricao: 'Ranking de recursos humanos informados por prestadores ativos.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'recursos',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'recursos-materiais',
            titulo: 'Recursos materiais por tipo',
            descricao: 'Ranking de recursos materiais informados por prestadores ativos.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'recursos',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'especialidades',
            titulo: 'Especialidades mais registradas',
            descricao: 'Ranking de especialidades e áreas vigentes registradas em prestadores ativos.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'sem-especialidade-obrigatoria',
            titulo: 'Sem especialidade vigente',
            descricao: 'Prestadores ativos sem nenhuma especialidade vigente registrada.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'capacidade-instalada-uf',
            titulo: 'Capacidade instalada por UF',
            descricao: 'Ranking de capacidade instalada declarada por prestadores ativos em cada UF.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'leitos',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'enderecos-ausentes',
            titulo: 'Endereço de correspondência ausente',
            descricao: 'Prestadores ativos sem endereço atual marcado como correspondência.',
            grupo: 'Pendências',
            classeGrupo: 'grupo-endereco',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'enderecos-desatualizados',
            titulo: 'Endereço de correspondência desatualizado',
            descricao: 'Prestadores ativos que possuem endereço de correspondência, porém sem nenhum marcado como atual.',
            grupo: 'Pendências',
            classeGrupo: 'grupo-endereco',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'pendencias-manuais',
            titulo: 'Pendências manuais por tipo',
            descricao: 'Ranking de pendências documentais registradas manualmente.',
            grupo: 'Pendências',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'pendências',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'pendencias-operacionais',
            titulo: 'Pendências operacionais',
            descricao: 'Pendências calculadas de endereço, certificado e diretoria.',
            grupo: 'Pendências',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'pendências',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'cnpj-duplicado',
            titulo: 'CNPJ duplicado',
            descricao: 'Cadastros envolvidos em CNPJ repetido na base de prestadores.',
            grupo: 'Pendências',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'cadastros',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'socios-faixa',
            titulo: 'Quadro societário por faixa de sócios',
            descricao: 'Prestadores ativos distribuídos pela quantidade de sócios vigentes.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'socios-cargo',
            titulo: 'Sócios por cargo',
            descricao: 'Distribuição dos sócios vigentes do quadro societário pelo cargo.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'sócios',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'filiais-matriz',
            titulo: 'Filiais por localização da matriz',
            descricao: 'Filiais ativas agrupadas pela matriz estar na mesma UF ou em outra.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'filiais',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'documento-constituicao',
            titulo: 'Documento de constituição',
            descricao: 'Prestadores ativos agrupados pelo tipo de documento de constituição.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'ensino',
            titulo: 'Estabelecimentos de ensino',
            descricao: 'Prestadores ativos agrupados por serem ou não estabelecimentos de ensino.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'gestao',
            titulo: 'Tipo de gestão',
            descricao: 'Prestadores ativos com gestão, agrupados pelo tipo: pública, privada ou terceirizada.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'abrangencia',
            titulo: 'Abrangência do serviço',
            descricao: 'Prestadores ativos agrupados pela abrangência de serviço informada.',
            grupo: 'Perfil jurídico',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'prestadores',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'movimentacao-situacao-pj',
            titulo: 'Movimentação de situação por mês',
            descricao: 'Volume mensal de mudanças de situação do prestador no ano selecionado.',
            grupo: 'Situação',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'movimentações',
            visualizacao: 'linha',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'pre-cadastros',
            titulo: 'Pré-cadastros de prestador pendentes',
            descricao: 'Backlog de pré-cadastros de prestador aguardando efetivação, por UF.',
            grupo: 'Situação',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'pré-cadastros',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'chefias-servico',
            titulo: 'Chefias de serviço por serviço',
            descricao: 'Ranking de serviços pelo total de prestadores ativos com chefia de serviço vigente.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'servicos-sem-chefia',
            titulo: 'Serviço que exige chefia sem chefia',
            descricao: 'Prestadores ativos com serviço que exige chefia, porém sem chefia vigente definida.',
            grupo: 'Serviços',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'corpo-clinico-entradas',
            titulo: 'Entradas no corpo clínico por mês',
            descricao: 'Volume mensal de entradas de médicos no corpo clínico no ano selecionado.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'entradas',
            visualizacao: 'linha',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'corpo-clinico-saidas',
            titulo: 'Saídas do corpo clínico por mês',
            descricao: 'Volume mensal de saídas de médicos do corpo clínico no ano selecionado.',
            grupo: 'Diretoria',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'saídas',
            visualizacao: 'linha',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'anotacoes-prestador-tipo',
            titulo: 'Anotações administrativas por tipo',
            descricao: 'Anotações no histórico do prestador agrupadas pelo tipo.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-operacional',
            rotuloTotal: 'anotações',
            visualizacao: 'barra',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'anotacoes-prestador-mes',
            titulo: 'Anotações administrativas por mês',
            descricao: 'Volume mensal de anotações no histórico do prestador no ano selecionado.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'anotações',
            visualizacao: 'linha',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'sem-cnes',
            titulo: 'Prestadores sem CNES',
            descricao: 'Prestadores ativos sem número CNES informado no cadastro.',
            grupo: 'Pendências',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'prestadores',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        }
    ];

    private readonly atualizacao$ = new Subject<void>();
    private readonly analiticoChange$ = new Subject<void>();
    private requestSeq = 0;
    private ufTokenRestrita: string | null = null;

    constructor(
        private prestadorControleAdministrativoService: PrestadorControleAdministrativoFeatureService,
        private authService: AuthService,
        private route: ActivatedRoute,
        private destroyRef: DestroyRef,
        private messageService: MessageService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        // A rota dispara a primeira carga: paramMap emite o valor corrente na inscricao.
        this.observarRota();
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUfSelecionada(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
        this.voltarParaEstatisticas();
        this.atualizarEstatisticas();
    }

    aplicarFiltros(): void {
        this.anoSelecionado = this.normalizarAno(this.anoSelecionado);
        this.voltarParaEstatisticas();
        this.atualizarEstatisticas();
    }

    obterAgrupadorSelecionado(): AgrupadorPrestador {
        return this.agrupadores.find((item) => item.slug === this.grupoSelecionadoSlug) ?? this.agrupadores[0];
    }

    subgrupoSelecionado = 'tudo';

    obterSubgrupoEstatistica(id: string): string {
        if (id.includes('cnae')) return 'cnae';
        if (id === 'servicos' || id === 'atividades') return 'servicos';
        if (id === 'especialidades' || id === 'sem-especialidade-obrigatoria') return 'especialidades';
        if (id === 'recursos-humanos' || id === 'recursos-materiais') return 'recursos';
        return 'outros';
    }

    obterEstatisticasGrupo(): EstatisticaPrestadorGrupo[] {
        const agrupador = this.obterAgrupadorSelecionado();
        let itens = this.estatisticas.filter((item) => item.grupo === agrupador.label);

        if (this.grupoSelecionadoSlug === 'servicos' && this.subgrupoSelecionado !== 'tudo') {
            itens = itens.filter((item) => this.obterSubgrupoEstatistica(item.id) === this.subgrupoSelecionado);
        }

        return itens;
    }

    private readonly ordemSecoes: { titulo: string; tipos: EstatisticaVisualizacao[] }[] = [
        { titulo: 'Séries e evolução', tipos: ['linha'] },
        { titulo: 'Distribuições', tipos: ['rosca', 'barra'] }
    ];

    private secoesCache: { chave: string; estatisticas: EstatisticaPrestadorGrupo[]; secoes: SecaoEstatisticaPrestador[] } | null = null;

    obterSecoesEstatisticas(): SecaoEstatisticaPrestador[] {
        // `this.estatisticas` recebe uma NOVA referência de array (e de cada grupo) a cada
        // resposta de consulta (ver atualizarGrupo/definirCarregando/resetarGrupos), então a
        // invalidação do cache precisa considerar essa referência além da chave slug+subgrupo.
        const chave = `${this.grupoSelecionadoSlug}|${this.subgrupoSelecionado}`;
        if (this.secoesCache && this.secoesCache.chave === chave && this.secoesCache.estatisticas === this.estatisticas) {
            return this.secoesCache.secoes;
        }

        const estatisticas = this.obterEstatisticasGrupo();
        const secoes = this.ordemSecoes
            .map((s) => ({
                titulo: s.titulo,
                mostrarTitulo: true,
                grupos: estatisticas.filter((g) => s.tipos.includes(g.visualizacao))
            }))
            .filter((s) => s.grupos.length > 0);
        // 1 seção só → sem cabeçalho (aparência atual)
        const resultado = secoes.length <= 1 ? secoes.map((s) => ({ ...s, mostrarTitulo: false })) : secoes;

        this.secoesCache = { chave, estatisticas: this.estatisticas, secoes: resultado };
        return resultado;
    }

    maximizarGrupo(grupo: EstatisticaPrestadorGrupo): void {
        this.grupoMaximizado = grupo;
        this.exibirModalMaximizados = true;
    }

    possuiAnalitico(grupo: EstatisticaPrestadorGrupo): boolean {
        return !ESTATISTICAS_SEM_ANALITICO_PRESTADOR.has(grupo.id) && this.obterOpcoesGrupoAnalitico(grupo).length > 0;
    }

    selecionarEstatisticaAnalitica(grupo: EstatisticaPrestadorGrupo): void {
        const primeiraOpcao = this.obterOpcoesGrupoAnalitico(grupo)[0];
        if (!primeiraOpcao?.codigo) {
            return;
        }

        this.analiticoChange$.next();
        this.estatisticaSelecionada = grupo;
        this.grupoAnaliticoCodigo = primeiraOpcao.codigo;
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnaliticoEstatistica();
    }

    voltarParaEstatisticas(): void {
        this.analiticoChange$.next();
        this.estatisticaSelecionada = null;
        this.grupoAnaliticoCodigo = null;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregandoAnalitico = false;
    }

    onGrupoAnaliticoChange(codigo: string): void {
        this.analiticoChange$.next();
        this.grupoAnaliticoCodigo = codigo;
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnaliticoEstatistica();
    }

    onPageChangeAnalitico(event: PaginatorState): void {
        this.analiticoChange$.next();
        this.firstAnalitico = event.first ?? 0;
        this.rowsAnalitico = event.rows ?? this.rowsAnalitico;
        this.carregarAnaliticoEstatistica();
    }

    obterOpcoesGrupoAnalitico(grupo: EstatisticaPrestadorGrupo | null = this.estatisticaSelecionada): PrestadorEstatisticaAgrupadaResponse[] {
        return (grupo?.itens ?? []).filter((item) => !!item.codigo && (item.total ?? 0) > 0);
    }

    obterIndicadorAnalitico(): IndicadorPendencia | null {
        if (!this.estatisticaSelecionada) {
            return null;
        }

        const grupoSelecionado = this.obterGrupoAnaliticoSelecionado();
        return {
            id: this.estatisticaSelecionada.id,
            titulo: this.estatisticaSelecionada.titulo,
            descricao: grupoSelecionado ? `${this.estatisticaSelecionada.descricao} Grupo: ${this.obterDescricaoItem(grupoSelecionado)}.` : this.estatisticaSelecionada.descricao,
            grupo: this.estatisticaSelecionada.grupo as IndicadorPendencia['grupo'],
            classeGrupo: this.estatisticaSelecionada.classeGrupo,
            quantidade: this.totalRegistrosAnalitico || null
        };
    }

    obterColunaDetalheAnalitico(): string | null {
        return this.estatisticaSelecionada ? 'Grupo selecionado' : null;
    }

    obterTotalGrupo(grupo: EstatisticaPrestadorGrupo | null): number | null {
        if (!grupo || grupo.itens === null) {
            return null;
        }

        if (grupo.modoTotal === 'primeiro') {
            return grupo.itens[0]?.total ?? 0;
        }

        if (grupo.modoTotal === 'media') {
            if (!grupo.itens.length) {
                return 0;
            }

            const soma = grupo.itens.reduce((total, item) => total + (item.total ?? 0), 0);
            return Math.round(soma / grupo.itens.length);
        }

        return grupo.itens.reduce((total, item) => total + (item.total ?? 0), 0);
    }

    obterDescricaoItem(item: PrestadorEstatisticaAgrupadaResponse): string {
        return item.descricao?.trim() || item.codigo?.trim() || 'Não informado';
    }

    obterComplementoItem(item: PrestadorEstatisticaAgrupadaResponse): string {
        const partes: string[] = [];

        if (item.ufSgUf && !this.ufSelecionada) {
            partes.push(item.ufSgUf);
        }

        return partes.join(' - ');
    }

    obterLarguraBarra(grupo: EstatisticaPrestadorGrupo, item: PrestadorEstatisticaAgrupadaResponse): string {
        const total = item.total ?? 0;
        const maiorTotal = this.obterMaiorTotal(grupo);

        if (!total || !maiorTotal) {
            return '0%';
        }

        return `${Math.max(6, Math.round((total / maiorTotal) * 100))}%`;
    }

    obterTipoGrafico(grupo: EstatisticaPrestadorGrupo): 'line' | 'bar' | 'doughnut' {
        if (grupo.visualizacao === 'linha') {
            return 'line';
        }

        if (grupo.visualizacao === 'rosca') {
            return 'doughnut';
        }

        return 'bar';
    }

    private graficoCache = new Map<
        EstatisticaPrestadorId,
        {
            itens: unknown;
            isDark: boolean;
            dados?: any;
            opcoesCard?: any;
            opcoesModal?: any;
        }
    >();

    private obterEntradaCacheGrafico(grupo: EstatisticaPrestadorGrupo) {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        let cache = this.graficoCache.get(grupo.id);
        if (!cache || cache.itens !== grupo.itens || cache.isDark !== isDark) {
            cache = { itens: grupo.itens, isDark };
            this.graficoCache.set(grupo.id, cache);
        }
        return cache;
    }

    obterDadosGrafico(grupo: EstatisticaPrestadorGrupo | null): any {
        if (!grupo?.itens?.length) {
            return null;
        }
        const cache = this.obterEntradaCacheGrafico(grupo);
        if (cache.dados === undefined) {
            cache.dados = this.computarDadosGrafico(grupo);
        }
        return cache.dados;
    }

    obterOpcoesGrafico(grupo: EstatisticaPrestadorGrupo | null, isModal = false): any {
        if (!grupo) {
            return this.computarOpcoesGrafico(grupo, isModal);
        }
        const cache = this.obterEntradaCacheGrafico(grupo);
        if (isModal) {
            if (cache.opcoesModal === undefined) {
                cache.opcoesModal = this.computarOpcoesGrafico(grupo, true);
            }
            return cache.opcoesModal;
        }
        if (cache.opcoesCard === undefined) {
            cache.opcoesCard = this.computarOpcoesGrafico(grupo, false);
        }
        return cache.opcoesCard;
    }

    private computarDadosGrafico(grupo: EstatisticaPrestadorGrupo): any {
        if (!grupo?.itens?.length) {
            return null;
        }

        const labels = grupo.itens.map((item) => this.obterDescricaoItem(item));
        const data = grupo.itens.map((item) => item.total ?? 0);
        const corBase = this.obterCorGrupo();

        if (grupo.visualizacao === 'linha') {
            return {
                labels,
                datasets: [
                    {
                        label: grupo.titulo,
                        data,
                        fill: false,
                        borderColor: corBase,
                        tension: 0.35
                    }
                ]
            };
        }

        if (grupo.visualizacao === 'rosca') {
            const backgroundColors = this.gerarCoresMonocromaticas(corBase, data.length);
            return {
                labels,
                datasets: [
                    {
                        data,
                        backgroundColor: backgroundColors,
                        hoverBackgroundColor: backgroundColors,
                        borderWidth: 1
                    }
                ]
            };
        }

        return {
            labels,
            datasets: [
                {
                    label: 'Total',
                    data,
                    backgroundColor: corBase,
                    borderRadius: 4
                }
            ]
        };
    }

    private computarOpcoesGrafico(grupo: EstatisticaPrestadorGrupo | null, isModal = false): any {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        const textColor = isDark ? '#f8fafc' : '#334155';
        const gridColor = isDark ? '#334155' : '#e2e8f0';

        if (grupo?.visualizacao === 'rosca') {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: isModal ? 'right' : 'bottom',
                        labels: {
                            color: textColor,
                            boxWidth: isModal ? 14 : 10,
                            padding: isModal ? 12 : 8,
                            font: { size: isModal ? 12 : 10 }
                        }
                    }
                }
            };
        }

        return {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: grupo?.visualizacao === 'barra' ? 'y' : 'x',
            plugins: {
                legend: {
                    display: false
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColor,
                        font: { size: isModal ? 11 : 9 }
                    },
                    grid: { color: grupo?.visualizacao === 'barra' ? gridColor : 'transparent' }
                },
                y: {
                    ticks: {
                        color: textColor,
                        font: { size: isModal ? 11 : 9 }
                    },
                    grid: { color: grupo?.visualizacao === 'barra' ? 'transparent' : gridColor }
                }
            }
        };
    }

    private atualizarEstatisticas(): void {
        this.atualizacao$.next();
        this.resetarGrupos();

        const requestId = this.iniciarAtualizacao();
        const uf = this.ufSelecionada ?? undefined;

        this.carregarGrupo('situacoes', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorSituacao(uf, this.limiteRanking), requestId);
        this.carregarGrupo('ativos-registro-cadastro', () => this.prestadorControleAdministrativoService.consultarAtivosPorRegistroCadastro(uf), requestId);
        this.carregarGrupo('novas-inscricoes', () => this.prestadorControleAdministrativoService.consultarNovasInscricoesPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('municipios', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorMunicipio(uf, this.limiteRanking), requestId);
        this.carregarGrupo('tipos-estabelecimento', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorTipoEstabelecimento(uf), requestId);
        this.carregarGrupo('categorias', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorCategoria(uf), requestId);
        this.carregarGrupo('naturezas', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorNatureza(uf), requestId);
        this.carregarGrupo('enderecos-ausentes', () => this.prestadorControleAdministrativoService.consultarEnderecosCorrespondenciaAusentes(uf), requestId);
        this.carregarGrupo('certificados-situacao', () => this.prestadorControleAdministrativoService.consultarCertificadosPorSituacao(uf), requestId);
        this.carregarGrupo('certificados-vencimento-faixa', () => this.prestadorControleAdministrativoService.consultarCertificadosPorFaixaVencimento(uf), requestId);
        this.carregarGrupo('renovacoes-certificado', () => this.prestadorControleAdministrativoService.consultarRenovacoesCertificadoPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('tempo-renovacao-certificado', () => this.prestadorControleAdministrativoService.consultarTempoMedioRenovacaoCertificado(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('sem-diretor-tecnico', () => this.prestadorControleAdministrativoService.consultarPrestadoresSemDiretorTecnico(uf), requestId);
        this.carregarGrupo('diretores-tecnicos-vinculos', () => this.prestadorControleAdministrativoService.consultarDiretoresTecnicosPorVinculos(uf), requestId);
        this.carregarGrupo('diretores-tecnicos-faixa-etaria', () => this.prestadorControleAdministrativoService.consultarDiretoresTecnicosPorFaixaEtaria(uf), requestId);
        this.carregarGrupo('diretoria-duplicada', () => this.prestadorControleAdministrativoService.consultarDiretoriaDuplicada(uf), requestId);
        this.carregarGrupo('razao-medicos-prestadores', () => this.prestadorControleAdministrativoService.consultarRazaoMedicosPrestadores(uf), requestId);
        this.carregarGrupo('multivinculo-corpo-clinico', () => this.prestadorControleAdministrativoService.consultarMultivinculoCorpoClinico(uf), requestId);
        this.carregarGrupo('corpo-clinico-tamanho', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorTamanhoCorpoClinico(uf), requestId);
        this.carregarGrupo('corpo-clinico-vinculo', () => this.prestadorControleAdministrativoService.consultarCorpoClinicoPorNaturezaVinculo(uf), requestId);
        this.carregarGrupo('comissao-etica-vigente', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorComissaoEticaVigente(uf), requestId);
        this.carregarGrupo('comissao-etica-vencimento', () => this.prestadorControleAdministrativoService.consultarComissaoEticaPorVencimento(uf), requestId);
        this.carregarGrupo('comissao-etica-obrigatoria-ausente', () => this.prestadorControleAdministrativoService.consultarComissaoEticaObrigatoriaAusente(uf), requestId);
        this.carregarGrupo('comissoes-obrigatorias-tipo', () => this.prestadorControleAdministrativoService.consultarComissoesObrigatoriasPorTipo(uf), requestId);
        this.carregarGrupo('cnaes-principais', () => this.prestadorControleAdministrativoService.consultarCnaesPrincipais(uf, this.limiteRanking), requestId);
        this.carregarGrupo('sem-cnae-principal', () => this.prestadorControleAdministrativoService.consultarPrestadoresSemCnaePrincipal(uf), requestId);
        this.carregarGrupo('cnaes-secundarios', () => this.prestadorControleAdministrativoService.consultarCnaesSecundariosPorPrestador(uf), requestId);
        this.carregarGrupo('servicos', () => this.prestadorControleAdministrativoService.consultarServicosMaisDeclarados(uf, this.limiteRanking), requestId);
        this.carregarGrupo('atividades', () => this.prestadorControleAdministrativoService.consultarAtividadesDesenvolvidas(uf, this.limiteRanking), requestId);
        this.carregarGrupo('recursos-humanos', () => this.prestadorControleAdministrativoService.consultarRecursosHumanosPorTipo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('recursos-materiais', () => this.prestadorControleAdministrativoService.consultarRecursosMateriaisPorTipo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('especialidades', () => this.prestadorControleAdministrativoService.consultarEspecialidadesMaisRegistradas(uf, this.limiteRanking), requestId);
        this.carregarGrupo('sem-especialidade-obrigatoria', () => this.prestadorControleAdministrativoService.consultarPrestadoresSemEspecialidadeObrigatoria(uf), requestId);
        this.carregarGrupo('capacidade-instalada-uf', () => this.prestadorControleAdministrativoService.consultarCapacidadeInstaladaPorUf(uf, this.limiteRanking), requestId);
        this.carregarGrupo('ciclo-vida-prestador', () => this.prestadorControleAdministrativoService.consultarCicloVidaPrestador(uf), requestId);
        this.carregarGrupo('pendencias-manuais', () => this.prestadorControleAdministrativoService.consultarPendenciasManuaisPorTipo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('pendencias-operacionais', () => this.prestadorControleAdministrativoService.consultarPendenciasOperacionaisPorTipo(uf), requestId);
        this.carregarGrupo('cnpj-duplicado', () => this.prestadorControleAdministrativoService.consultarDuplicidadesCnpj(uf), requestId);
        this.carregarGrupo('faixa-capital-social', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorFaixaCapitalSocial(uf), requestId);
        this.carregarGrupo('delegacias', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorDelegacia(uf, this.limiteRanking), requestId);
        this.carregarGrupo('vinculados-debito', () => this.prestadorControleAdministrativoService.consultarVinculadosEmDebito(uf), requestId);
        this.carregarGrupo('enderecos-desatualizados', () => this.prestadorControleAdministrativoService.consultarEnderecosCorrespondenciaDesatualizados(uf), requestId);
        this.carregarGrupo('eleitorado-corpo-clinico', () => this.prestadorControleAdministrativoService.consultarEleitoradoPorCorpoClinico(uf, this.limiteRanking), requestId);
        this.carregarGrupo('socios-faixa', () => this.prestadorControleAdministrativoService.consultarSociosPorFaixa(uf), requestId);
        this.carregarGrupo('socios-cargo', () => this.prestadorControleAdministrativoService.consultarSociosPorCargo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('filiais-matriz', () => this.prestadorControleAdministrativoService.consultarFiliaisPorLocalizacaoMatriz(uf), requestId);
        this.carregarGrupo('documento-constituicao', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorDocumentoConstituicao(uf, this.limiteRanking), requestId);
        this.carregarGrupo('ensino', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorEnsino(uf), requestId);
        this.carregarGrupo('gestao', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorGestao(uf, this.limiteRanking), requestId);
        this.carregarGrupo('abrangencia', () => this.prestadorControleAdministrativoService.consultarPrestadoresPorAbrangencia(uf, this.limiteRanking), requestId);
        this.carregarGrupo('movimentacao-situacao-pj', () => this.prestadorControleAdministrativoService.consultarMovimentacaoSituacaoPjPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('pre-cadastros', () => this.prestadorControleAdministrativoService.consultarPreCadastrosPrestadorPorUf(uf, this.limiteRanking), requestId);
        this.carregarGrupo('chefias-servico', () => this.prestadorControleAdministrativoService.consultarChefiasPorServico(uf, this.limiteRanking), requestId);
        this.carregarGrupo('servicos-sem-chefia', () => this.prestadorControleAdministrativoService.consultarServicosSemChefia(uf), requestId);
        this.carregarGrupo('corpo-clinico-entradas', () => this.prestadorControleAdministrativoService.consultarCorpoClinicoEntradasPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('corpo-clinico-saidas', () => this.prestadorControleAdministrativoService.consultarCorpoClinicoSaidasPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('anotacoes-prestador-tipo', () => this.prestadorControleAdministrativoService.consultarAnotacoesPrestadorPorTipo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('anotacoes-prestador-mes', () => this.prestadorControleAdministrativoService.consultarAnotacoesPrestadorPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('sem-cnes', () => this.prestadorControleAdministrativoService.consultarPrestadoresSemCnes(uf), requestId);
    }

    private carregarAnaliticoEstatistica(): void {
        if (!this.estatisticaSelecionada || !this.grupoAnaliticoCodigo) {
            return;
        }

        const estatisticaId = this.estatisticaSelecionada.id;
        const codigo = this.grupoAnaliticoCodigo;
        const { page, size } = this.obterPaginacaoAnalitica();

        this.carregandoAnalitico = true;
        this.prestadorControleAdministrativoService
            .consultarPrestadoresEstatisticaCadastro(estatisticaId, this.ufSelecionada, this.anoSelecionado, page, size, codigo)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.analiticoChange$),
                // markForCheck: estado assíncrono gravado em campos não-signal; sob o layout OnPush o
                // tick da resposta HTTP não alcança este painel (só renderizava após um clique).
                finalize(() => {
                    if (this.estatisticaSelecionada?.id === estatisticaId && this.grupoAnaliticoCodigo === codigo) {
                        this.carregandoAnalitico = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.estatisticaSelecionada?.id !== estatisticaId || this.grupoAnaliticoCodigo !== codigo) {
                        return;
                    }

                    this.linhasAnalitico = (resp?.content ?? []).map((item) => this.mapLinhaAnalitica(item));
                    this.totalRegistrosAnalitico = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.estatisticaSelecionada?.id !== estatisticaId || this.grupoAnaliticoCodigo !== codigo) {
                        return;
                    }

                    this.linhasAnalitico = [];
                    this.totalRegistrosAnalitico = 0;
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar o relatório analítico da estatística.' });
                }
            });
    }

    private carregarGrupo(id: EstatisticaPrestadorId, consulta: () => Observable<PrestadorEstatisticaAgrupadaResponse[]>, requestId: number): void {
        if (!this.pertenceAoAgrupadorSelecionado(id)) {
            this.definirCarregando(id, false);
            return;
        }

        consulta()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.atualizacao$),
                finalize(() => {
                    if (this.isRequestAtual(requestId)) {
                        this.definirCarregando(id, false);
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (itens) => {
                    const normalizados = this.normalizarItens(itens);
                    const finais = ESTATISTICAS_MENSAIS_PRESTADOR.has(id) ? this.preencherMesesVazios(normalizados) : normalizados;
                    this.atualizarGrupo(id, finais, requestId);
                },
                error: () => {
                    this.atualizarGrupo(id, [], requestId);
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar estatísticas de prestador.' });
                }
            });
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private observarRota(): void {
        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const slug = params.get('grupo') ?? this.agrupadores[0].slug;
            this.grupoSelecionadoSlug = this.agrupadores.some((item) => item.slug === slug) ? slug : this.agrupadores[0].slug;
            this.subgrupoSelecionado = 'tudo';
            this.voltarParaEstatisticas();
            // Cada aba carrega apenas as suas consultas; trocar de aba busca as da aba nova.
            this.atualizarEstatisticas();
            this.cdr.markForCheck();
        });
    }

    /** Evita disparar as consultas dos outros agrupadores ao abrir uma aba. */
    private pertenceAoAgrupadorSelecionado(id: EstatisticaPrestadorId): boolean {
        const estatistica = this.estatisticas.find((grupo) => grupo.id === id);
        return estatistica?.grupo === this.obterAgrupadorSelecionado().label;
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR') {
            return this.ufTokenRestrita;
        }

        if (!/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }

    private normalizarAno(valor: number): number {
        const ano = Number(valor);

        if (!Number.isFinite(ano)) {
            return this.anoAtual;
        }

        return Math.min(Math.max(Math.trunc(ano), 1900), this.anoAtual + 1);
    }

    private normalizarItens(itens: PrestadorEstatisticaAgrupadaResponse[] | null | undefined): PrestadorEstatisticaAgrupadaResponse[] {
        return (itens ?? [])
            .map((item) => ({
                ...item,
                codigo: item.codigo?.trim() || null,
                descricao: item.descricao?.trim() || item.codigo?.trim() || 'Não informado',
                ufSgUf: item.ufSgUf?.trim() || null,
                ordem: item.ordem ?? null,
                total: item.total ?? 0
            }))
            .sort((a, b) => {
                const ordemA = a.ordem ?? Number.MAX_SAFE_INTEGER;
                const ordemB = b.ordem ?? Number.MAX_SAFE_INTEGER;

                if (ordemA !== ordemB) {
                    return ordemA - ordemB;
                }

                return (a.descricao ?? '').localeCompare(b.descricao ?? '');
            });
    }

    /**
     * A API só devolve linha para o mês que teve movimento. Sem completar os meses zerados,
     * a série mensal termina no último mês com dado e a linha de tendência engana.
     */
    private preencherMesesVazios(itens: PrestadorEstatisticaAgrupadaResponse[]): PrestadorEstatisticaAgrupadaResponse[] {
        const ano = this.anoSelecionado;
        const ultimoMes = ano === this.anoAtual ? new Date().getMonth() + 1 : 12;
        const porCodigo = new Map(itens.map((item) => [item.codigo ?? '', item]));

        const meses: PrestadorEstatisticaAgrupadaResponse[] = [];
        for (let mes = 1; mes <= ultimoMes; mes++) {
            const mesFormatado = String(mes).padStart(2, '0');
            const codigo = `${ano}-${mesFormatado}`;
            meses.push(
                porCodigo.get(codigo) ?? {
                    codigo,
                    descricao: `${mesFormatado}/${ano}`,
                    ufSgUf: null,
                    ordem: Number(`${ano}${mesFormatado}`),
                    total: 0
                }
            );
        }

        // Preserva linhas com código fora do padrão AAAA-MM, se houver.
        const foraDoPadrao = itens.filter((item) => !/^\d{4}-\d{2}$/.test(item.codigo ?? ''));
        return [...meses, ...foraDoPadrao];
    }

    private resetarGrupos(): void {
        this.estatisticas = this.estatisticas.map((grupo) => ({
            ...grupo,
            itens: null,
            carregando: true
        }));
    }

    private iniciarAtualizacao(): number {
        this.requestSeq += 1;
        return this.requestSeq;
    }

    private isRequestAtual(requestId: number): boolean {
        return requestId === this.requestSeq;
    }

    private definirCarregando(id: EstatisticaPrestadorId, carregando: boolean): void {
        this.estatisticas = this.estatisticas.map((grupo) => (grupo.id === id ? { ...grupo, carregando } : grupo));

        if (this.grupoMaximizado?.id === id) {
            this.grupoMaximizado = this.estatisticas.find((grupo) => grupo.id === id) ?? null;
        }
    }

    private atualizarGrupo(id: EstatisticaPrestadorId, itens: PrestadorEstatisticaAgrupadaResponse[], requestId: number): void {
        if (!this.isRequestAtual(requestId)) {
            return;
        }

        const itensCompletos = ESTATISTICAS_MENSAIS_PRESTADOR.has(id) ? EstatisticaMensalUtils.preencherMesesAusentes(itens) : itens;

        this.estatisticas = this.estatisticas.map((grupo) => (grupo.id === id ? { ...grupo, itens: itensCompletos, carregando: false } : grupo));

        if (this.grupoMaximizado?.id === id) {
            this.grupoMaximizado = this.estatisticas.find((grupo) => grupo.id === id) ?? null;
        }
    }

    private obterGrupoAnaliticoSelecionado(): PrestadorEstatisticaAgrupadaResponse | null {
        if (!this.estatisticaSelecionada || !this.grupoAnaliticoCodigo) {
            return null;
        }

        return this.obterOpcoesGrupoAnalitico(this.estatisticaSelecionada).find((item) => item.codigo === this.grupoAnaliticoCodigo) ?? null;
    }

    private obterPaginacaoAnalitica(): { page: number; size: number } {
        const size = this.rowsAnalitico;
        const page = size > 0 ? Math.floor(this.firstAnalitico / size) : 0;
        return { page, size };
    }

    private mapLinhaAnalitica(item: PrestadorEstatisticaCadastroAnaliticoResponse): PendenciaLinha {
        const grupoSelecionado = this.obterGrupoAnaliticoSelecionado();
        return {
            uf: item.ufSgUf ?? '',
            crm: this.formatarCnpj(item.nuCnpj),
            nome: item.nmSolicitante ?? '',
            situacao: item.dsSituacao ?? item.inSituacao ?? '',
            codigoSituacao: item.inSituacao ?? '',
            detalhe: item.informacaoComplementar ?? (grupoSelecionado ? this.obterDescricaoItem(grupoSelecionado) : '')
        };
    }

    private formatarCnpj(cnpj?: string | null): string {
        const digitos = String(cnpj ?? '').replace(/\D/g, '');

        if (digitos.length !== 14) {
            return digitos || '-';
        }

        return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
    }

    private obterMaiorTotal(grupo: EstatisticaPrestadorGrupo): number {
        return Math.max(...(grupo.itens ?? []).map((item) => item.total ?? 0), 0);
    }

    private obterCorGrupo(): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#3b82f6';
    }

    private gerarCoresMonocromaticas(hexCor: string, totalItens: number): string[] {
        const corPadrao = /^#[0-9a-f]{6}$/i.test(hexCor) ? hexCor : '#3b82f6';
        const r = parseInt(corPadrao.slice(1, 3), 16);
        const g = parseInt(corPadrao.slice(3, 5), 16);
        const b = parseInt(corPadrao.slice(5, 7), 16);

        return Array.from({ length: totalItens }, (_, index) => {
            const opacity = Math.max(0.24, 1 - index * 0.14);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        });
    }
}
